import React, { useEffect } from 'react';
import { useAnimatedNavigate } from '../../../../hooks/useAnimatedNavigate';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useBillingStore } from '../../store/billingStore';
import { useBilling } from '../../hooks/useBilling';
import ThemeCard from './ThemeCard';

const ThemeListPage: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const { fetchThemes, duplicateTheme, setDefaultTheme, deleteTheme } = useBilling();
  const themes = useBillingStore((s) => s.themes);
  const loading = useBillingStore((s) => s.loading);

  useEffect(() => {
    fetchThemes().catch(() => {});
  }, []);

  const handleEdit = (themeId: string) => {
    navigate(`/billing/themes/${themeId}/edit`);
  };

  const handleDuplicate = async (themeId: string) => {
    const theme = themes.find((t) => t.theme_id === themeId);
    const name = theme ? `${theme.name} (Copy)` : 'Duplicated Theme';
    try {
      await duplicateTheme(themeId, name);
      await fetchThemes();
    } catch {
      // error handled by hook
    }
  };

  const handleSetDefault = async (themeId: string) => {
    try {
      await setDefaultTheme(themeId);
      await fetchThemes();
    } catch {
      // error handled by hook
    }
  };

  const handleDelete = async (themeId: string) => {
    if (!window.confirm('Are you sure you want to delete this theme?')) return;
    try {
      await deleteTheme(themeId);
      await fetchThemes();
    } catch {
      // error handled by hook
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 lex-tab-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/billing')}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Billing
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invoice Themes</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Customize how your invoices look when previewed and downloaded as PDF.
          </p>
        </div>
        <button
          onClick={() => navigate('/billing/themes/new')}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Create New Theme
        </button>
      </div>

      {/* Theme grid */}
      {loading && themes.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-gray-500 dark:text-gray-400">Loading themes...</span>
        </div>
      ) : themes.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-500 dark:text-gray-400 mb-4">No invoice themes yet.</p>
          <button
            onClick={() => navigate('/billing/themes/new')}
            className="px-4 py-2 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover"
          >
            Create Your First Theme
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {themes.map((theme) => (
            <ThemeCard
              key={theme.theme_id}
              theme={theme}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ThemeListPage;
